/**
 * BuddyGroupManageMembers — Combined member list + invite flow.
 *
 * ALL new members are added via invitation only. The primary cardholder
 * searches for users and sends invitations. Invitees accept/decline from
 * their pending invitations page. The member list shows both accepted
 * members and pending invitations with status badges.
 */

import { useState } from 'react';
import { AlertCircle, Loader2, Mail } from 'lucide-react';
import { BuddyGroupMemberList } from './BuddyGroupMemberList';
import { UserSearchInput } from './UserSearchInput';
import { useBuddyGroupMutations } from '@/hooks/useBuddyGroupMembers';
import { useSendInvitation, useCancelInvitation } from '@/hooks/useBuddyGroupInvitations';
import { useToast } from '@/contexts/ToastContext';
import type { BuddyGroupMemberView } from '@/types/buddy-groups';
import type {
  SearchUserResult,
  InviteePartneredEntryPreview,
} from '@/services/buddy-group.service';
import { previewInviteePartneredEntries } from '@/services/buddy-group.service';
import { BUDDY_GROUP_MAX_SIZE } from '@/types/buddy-groups';
import { Button } from '@/components/ui/button';

interface BuddyGroupManageMembersProps {
  groupId: string;
  rodeoId?: string;
  members: BuddyGroupMemberView[];
  currentUserProfileId: string;
  isPrimaryUser: boolean;
  maxSize?: number;
}

export function BuddyGroupManageMembers({
  groupId,
  rodeoId = '',
  members,
  currentUserProfileId,
  isPrimaryUser,
  maxSize = BUDDY_GROUP_MAX_SIZE,
}: BuddyGroupManageMembersProps) {
  const { removeMember } = useBuddyGroupMutations();
  const sendInvitation = useSendInvitation();
  const cancelInvitation = useCancelInvitation();
  const { showSuccess, showError } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  // Pending-invite preview state (Option A S1 heads-up). Holds the
  // selected user + their decoupled pairs while we wait for the inviter
  // to confirm sending. Empty preview = no heads-up needed; we send the
  // invite directly without showing the panel.
  const [pendingInvite, setPendingInvite] = useState<{
    user: SearchUserResult;
    decoupledEntries: InviteePartneredEntryPreview[];
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isFull = members.length >= maxSize;
  const currentMemberIds = members.map((m) => m.user_profile_id);

  const sendInviteFor = async (user: SearchUserResult) => {
    setError(null);
    try {
      await sendInvitation.mutateAsync({
        buddyGroupId: groupId,
        inviteeProfileId: user.user_profile_id,
      });
      showSuccess('Invitation sent', `Invited ${user.display_name} to join this group.`);
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to send invitation';
      if (msg.includes('409') || msg.includes('already') || msg.includes('duplicate') || msg.includes('conflict')) {
        setError('This user is already invited or already a member.');
      } else {
        showError('Failed to send invitation', msg);
        setError(msg);
      }
    }
  };

  const handleInviteUser = async (user: SearchUserResult) => {
    setError(null);
    setPendingInvite(null);
    setPreviewLoading(true);
    try {
      // Per Option A "decoupled pair" rule: preview any roping entries
      // the invitee already has in this group's rodeo(s) where the
      // partner isn't a group member. Those entries will stay decoupled
      // even after they accept, so the inviter sees them up front.
      const entries = await previewInviteePartneredEntries(
        groupId,
        user.user_profile_id,
      );
      if (entries.length === 0) {
        await sendInviteFor(user);
      } else {
        // Pause and surface the heads-up; the inviter clicks "Send"
        // (or Cancel) on the inline panel below.
        setPendingInvite({ user, decoupledEntries: entries });
      }
    } catch (err: any) {
      // Preview failure is non-fatal — degrade to direct send so a
      // dropped /invite-preview never blocks invitations.
      console.warn('[BuddyGroupManageMembers] invite preview failed:', err?.message);
      await sendInviteFor(user);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmPendingInvite = async () => {
    if (!pendingInvite) return;
    const user = pendingInvite.user;
    setPendingInvite(null);
    await sendInviteFor(user);
  };

  const cancelPendingInvite = () => {
    setPendingInvite(null);
  };

  const handleRemoveMember = async (memberId: string) => {
    setError(null);
    setRemovingMemberId(memberId);
    try {
      // Check if this is a pending invitation or an accepted member
      const member = members.find((m) => m.id === memberId);
      if (member?.invitation_status === 'pending') {
        await cancelInvitation.mutateAsync(memberId);
      } else {
        await removeMember.mutateAsync({ groupId, memberId });
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          Members ({members.length}/{maxSize})
        </h3>
      </div>

      {/* Invite search — primary cardholder only, group not full */}
      {isPrimaryUser && !isFull && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-xs font-medium text-muted-foreground">
              Invite a Member
            </p>
          </div>
          <UserSearchInput
            onSelect={handleInviteUser}
            placeholder="Search by name or member number..."
            disabled={sendInvitation.isPending || previewLoading || !!pendingInvite}
            excludeUserIds={currentMemberIds}
            rodeoId={rodeoId}
            excludeGroupId={groupId}
          />
          {previewLoading && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Checking for partnered entries...
            </div>
          )}
          {sendInvitation.isPending && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Sending invitation...
            </div>
          )}
          {/*
            Option A S1 heads-up: invitee already has team-roping entries
            in this group's rodeo(s) with someone outside the group.
            Per Option A those entries stay decoupled (governed by the pair,
            not the group) even after they join — so we surface that
            BEFORE the invite goes out and let the primary confirm.
          */}
          {pendingInvite && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2 text-sm">
              <div className="font-medium text-amber-700 dark:text-amber-300">
                Heads up — some of {pendingInvite.user.display_name}'s
                entries won't follow this group's preferences.
              </div>
              <p className="text-xs text-muted-foreground">
                These team roping pairs include someone outside the group,
                so they'll be drawn separately even after they join:
              </p>
              <ul className="text-xs space-y-1">
                {pendingInvite.decoupledEntries.map((entry) => (
                  <li
                    key={entry.registration_id}
                    className="flex flex-wrap items-baseline gap-x-1 text-muted-foreground"
                  >
                    <span className="font-medium text-foreground">
                      {entry.event_name ?? 'Team roping'}
                    </span>
                    <span>with</span>
                    <span className="font-medium text-foreground">
                      {entry.partner_name ?? 'an outside partner'}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={confirmPendingInvite}
                  disabled={sendInvitation.isPending}
                >
                  Send invite anyway
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={cancelPendingInvite}
                  disabled={sendInvitation.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {isFull && (
        <p className="text-xs text-muted-foreground">
          Group is at maximum capacity ({maxSize} members).
        </p>
      )}

      {error && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <BuddyGroupMemberList
        members={members}
        currentUserProfileId={currentUserProfileId}
        isPrimaryUser={isPrimaryUser}
        onRemoveMember={handleRemoveMember}
        removingMemberId={removingMemberId}
      />
    </div>
  );
}
